import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, ShieldCheck, DollarSign, Star, Loader, AlertTriangle, CheckCircle, Info } from 'lucide-react';

const NotificationIcon = ({ type }) => {
    const iconProps = { size: 24, className: "flex-shrink-0" };
    switch (type) {
        case 'security':
            return <ShieldCheck {...iconProps} className="text-red-400" />;
        case 'deposit':
            return <DollarSign {...iconProps} className="text-green-400" />;
        case 'rating':
            return <Star {...iconProps} className="text-yellow-400" />;
        default:
            return <Info {...iconProps} className="text-blue-400" />;
    }
};

const NotificationItem = ({ notification, index }) => (
    <motion.li
        className="flex items-start gap-4 py-4 px-5 rounded-xl transition-colors hover:bg-white/5"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
    >
        <NotificationIcon type={notification.type} />
        <div className="flex-1">
            <p className="text-slate-200">{notification.message}</p>
            <p className="text-xs text-slate-500 mt-1">
                {new Date(notification.createdAt).toLocaleString()}
            </p>
        </div>
        {!notification.read && (
            <div className="w-2.5 h-2.5 rounded-full bg-cyan-400 self-center animate-pulse"></div>
        )}
    </motion.li>
);

const Notification = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { token } = useAuth();

    useEffect(() => {
        const fetchNotifications = async () => {
            if (!token) {
                setError('Authentication required.');
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                const res = await axios.get('/api/notifications', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                // Sort by most recent
                setNotifications(res.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
            } catch (err) {
                setError('Could not fetch notifications.');
            } finally {
                setLoading(false);
            }
        };

        fetchNotifications();
    }, [token]);
    
    const markAllAsRead = async () => {
        if (!token) return;
        try {
            await axios.put('/api/notifications/mark-all-read', {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            // Re-fetch notifications to update state and badge
            const res = await axios.get('/api/notifications', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setNotifications(res.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
        } catch (err) {
            console.error('Failed to mark all as read:', err);
        }
    };


    return (
        <>
        <Helmet>
            <title>Notifications | RatingApp</title>
            <meta name="description" content="View your latest notifications and platform updates on RatingApp." />
        </Helmet>
        <div className="w-full pb-28 from-gray-900 via-blue-900/30 to-gray-900 text-white font-sans p-4">
            <div className="max-w-2xl mx-auto">
                <motion.div 
                    className="flex justify-between items-center mb-8"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <h1 className="text-4xl font-extrabold tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500">
                        Notifications
                    </h1>
                     <button 
                        onClick={markAllAsRead}
                        className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                    >
                        Mark all as read
                    </button>
                </motion.div>
                
                <div className="bg-gray-800/20 backdrop-blur-lg border border-white/10 rounded-2xl shadow-lg">
                    {loading && (
                        <div className="flex justify-center items-center gap-2 py-20 text-slate-400">
                            <Loader className="animate-spin" />
                            <span>Loading notifications...</span>
                        </div>
                    )}
                    {error && (
                        <div className="flex justify-center items-center gap-2 py-20 text-red-400">
                            <AlertTriangle />
                            <span>{error}</span>
                        </div>
                    )}
                    {!loading && !error && (
                        notifications.length === 0 ? (
                            <div className="flex flex-col justify-center items-center gap-4 py-20 text-slate-500">
                                <Bell size={40} />
                                <h3 className="font-bold text-lg">All caught up!</h3>
                                <p>You have no new notifications.</p>
                            </div>
                        ) : (
                            <ul className="divide-y divide-white/10">
                                <AnimatePresence>
                                    {notifications.map((notification, index) => (
                                        <NotificationItem key={notification._id} notification={notification} index={index} />
                                    ))}
                                </AnimatePresence>
                            </ul>
                        )
                    )}
                </div>
            </div>
        </div>
        </>
    );
};

export default Notification; 